import React from 'react';
import { RiSmartphoneLine } from '@remixicon/react';
import { MainButton } from '../shared/MainButton';
import type { Order } from '@/lib/OrderApi';
import { isInVisitorApp } from '@/lib/visitorAppWebView';
import * as m from '@/paraglide/messages.js';

/**
 * FAS-974 — "Open in app" for events that run a visitor app next to our ticketing.
 *
 * The link is the api's own landing page for this order: on a phone with the app
 * installed it opens the order straight in the app (Universal/App Link); without
 * the app it shows a branded page with the store links. The card only renders
 * when the api says the event has an app — everyone else never sees it.
 *
 * It also stays hidden inside the app's own in-app WebView, where "open in app"
 * is a dead end: the visitor is already there.
 */

interface Props {
    order: Order;
}

const OpenInAppCard: React.FC<Props> = ({ order }) => {
    const app = order.visitorApp;
    if (!app || isInVisitorApp()) {
        return null;
    }

    return (
        <div
            className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] rounded-md mt-4 px-4 sm:px-8 py-4'
            data-testid='open-in-app'
        >
            <div className='flex items-start gap-3'>
                <RiSmartphoneLine className='w-5 h-5 flex-shrink-0 mt-0.5' />
                <div className='flex-1'>
                    <h3 className='text-lg font-semibold'>{m.open_in_app_title()}</h3>
                    <p className='text-sm mt-1 opacity-80'>{m.open_in_app_body({ appName: app.name })}</p>
                </div>
            </div>
            <MainButton className='w-full mt-4' url={app.url}>
                <div className='flex items-center justify-center gap-2'>
                    <RiSmartphoneLine className='w-5 h-5' />
                    {m.open_in_app_button({ appName: app.name })}
                </div>
            </MainButton>
        </div>
    );
};

export default OpenInAppCard;
